import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Image,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import calendarIcon from "../../../assets/images/onleave/calendar.svg";
import SearchableSelectMulti from "../../../components/selectForm/mutiSelect";
import DeleteDialog from "../../../components/dialog/delete";
import { useCategories } from "../../../categorys/jobTitle/index";
import { attendanceCall } from "../../../api/attendance";
import { exportFile } from "../../../utils/tools";
import { EMPLOYEE_DEPARTMENT } from "../../../utils/contants";

// Không chọn loại nhân viên => cả khối kinh doanh và hỗ trợ kinh doanh
const DEFAULT_DEPARTMENTS = [
  EMPLOYEE_DEPARTMENT.SUPPORT_BUSINESS,
  EMPLOYEE_DEPARTMENT.BUSINESS,
];

// Cột file xuất khi chốt bảng công (khác nút Xuất excel: thêm Trưởng khối,
// Trưởng vùng, Công chuẩn; bỏ Tỉ lệ phí dịch vụ)
const lockExportColumnMapping = {
  date: "Thời gian",
  codeEmployee: "Mã nhân viên",
  fullName: "Tên nhân viên",
  saleBlock: "Khối",
  saleArea: "Vùng",
  saleBlockManager: "Trưởng khối",
  saleAreaManager: "Trưởng vùng",
  jobPosition: "Chức vụ",
  workingDays: "Ngày công",
  totalStandardWorking: "Công chuẩn",
  businessCategory: "Phân loại",
};

const FormLockAttendance = ({ isOpen, onClose, onLocked }) => {
  const [date, setDate] = useState(new Date());
  const [departmentIds, setDepartmentIds] = useState([]);
  // null = chưa xuất dữ liệu với bộ lọc hiện tại
  const [exportedCount, setExportedCount] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { constantData } = useCategories(null);

  const departments = useMemo(
    () =>
      Object.entries(constantData?.departmentByNumbers || {}).map(
        ([key, value]) => ({ key, value }),
      ),
    [constantData?.departmentByNumbers],
  );

  const month = date ? date.getMonth() + 1 : null;
  const year = date ? date.getFullYear() : null;
  const selectedDepartments = departmentIds.length
    ? departmentIds.map(Number)
    : DEFAULT_DEPARTMENTS;
  const departmentLabel =
    departments
      .filter((d) => selectedDepartments.includes(Number(d.key)))
      .map((d) => d.value)
      .join(", ") || "Kinh doanh, Hỗ trợ kinh doanh";

  // Đổi bộ lọc thì phải xuất lại dữ liệu trước khi chốt
  const handleChangeDate = (value) => {
    setDate(value);
    setExportedCount(null);
  };

  const handleChangeDepartments = (value) => {
    setDepartmentIds(value);
    setExportedCount(null);
  };

  const handleExport = async () => {
    if (!date) {
      toast.error("Vui lòng chọn tháng");
      return;
    }

    setIsExporting(true);
    try {
      const data = await attendanceCall.exportByMonth({
        date: new Date(year, month - 1, 1),
        departments: selectedDepartments.join(","),
      });

      if (data?.length) {
        exportFile(
          data,
          lockExportColumnMapping,
          `bang_cong_va_chuc_danh_thang_${month}_${year}.xlsx`,
        );
        toast.success("Download file thành công");
        setExportedCount(data.length);
      } else {
        toast.error("Không có dữ liệu");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleLock = async () => {
    if (isLocking) return;

    setIsLocking(true);
    const toastId = toast.loading("Đang chốt công...");
    try {
      const result = await attendanceCall.lock(
        month,
        year,
        selectedDepartments,
      );
      toast.success(
        `Chốt công tháng ${month}/${year} thành công (${result?.totalCount || 0} bản ghi)`,
      );
      setIsConfirmOpen(false);
      onLocked?.();
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      toast.dismiss(toastId);
      setIsLocking(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent
          backgroundColor="var(--color-main)"
          borderRadius={"16px"}
          padding={"8px"}
          maxW={"460px"}
        >
          <ModalHeader
            fontSize={"18px"}
            fontWeight={600}
            color={"var(--color-header-employee)"}
          >
            Chốt bảng công
          </ModalHeader>
          <ModalBody>
            <FormControl mb={"16px"}>
              <FormLabel
                fontSize={"12px"}
                fontWeight={400}
                color={"var(--color-label-employee)"}
                mb={"6px"}
              >
                Tháng / Năm
              </FormLabel>
              <InputGroup>
                <DatePicker
                  selected={date}
                  onChange={handleChangeDate}
                  dateFormat="MM/yyyy"
                  locale="vi"
                  showMonthYearPicker
                  placeholderText="mm/yyyy"
                  className="datePickerStyle"
                  portalId="datepicker-portal"
                  popperProps={{ strategy: "fixed" }}
                  style={{ width: "100%" }}
                />
                <InputRightElement pointerEvents="none" h={"44px"}>
                  <Image src={calendarIcon} />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel
                fontSize={"12px"}
                fontWeight={400}
                color={"var(--color-label-employee)"}
                mb={"6px"}
              >
                Loại nhân viên
              </FormLabel>
              <SearchableSelectMulti
                isShowSearch={false}
                options={departments}
                placeholder="Mặc định: Kinh doanh và Hỗ trợ kinh doanh"
                setValue={handleChangeDepartments}
                width={"100%"}
              />
            </FormControl>

            {exportedCount !== null && (
              <Box
                mt={"16px"}
                p={"12px"}
                borderRadius={"8px"}
                backgroundColor={"#F9FAFD"}
              >
                <Text fontSize={"14px"} color={"#293755"}>
                  Đã xuất {exportedCount} bản ghi tháng {month}/{year} (
                  {departmentLabel}). Kiểm tra file rồi bấm "Chốt công" để
                  khoá bảng công.
                </Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter gap={"12px"}>
            <Button
              onClick={onClose}
              variant={"ghost"}
              borderRadius={"8px"}
              h={"44px"}
              fontSize={"15px"}
            >
              Hủy
            </Button>
            <Button
              onClick={handleExport}
              isLoading={isExporting}
              loadingText="Đang xuất dữ liệu..."
              borderRadius={"8px"}
              backgroundImage="var(--linear-gradient-excel)"
              color={"var(--color-main)"}
              h={"44px"}
              fontSize={"15px"}
              _hover={{ filter: "brightness(90%)" }}
            >
              Xuất dữ liệu
            </Button>
            {exportedCount !== null && (
              <Button
                onClick={() => setIsConfirmOpen(true)}
                isLoading={isLocking}
                borderRadius={"8px"}
                background={"var(--linear-gradient-employee)"}
                color={"var(--color-main)"}
                h={"44px"}
                fontSize={"15px"}
                _hover={{ filter: "brightness(90%)" }}
              >
                Chốt công
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <DeleteDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title="Xác nhận chốt công"
        description={`Bạn có chắc chắn chốt công tháng ${month}/${year} (${departmentLabel}) không? Nếu chốt thì sẽ không thay đổi được bản ghi chốt công nữa.`}
        textDelete="Đồng ý"
        action={handleLock}
      />
    </>
  );
};

export default FormLockAttendance;
